'use client'
import { useMemo } from 'react'
import { RaButton, RaIcon } from '@core'

import { ICONS } from '@constants/icons.constants'
import { BuildingBlockPostType, RoundtripPostType } from '@typings'

import { useSettings } from '@/contexts/settings.context'
import { useWishlist } from '@/contexts/wishlist.context'
import { getCurrentEnv } from '@/utilities/helpers/get-current-env.helpers'
import { makeCartItem } from '@/utilities/helpers/make-cart-item.helpers'
import { translate } from '@/utilities/helpers/translate.helpers'

interface RaButtonWishlistProps {
  product: BuildingBlockPostType | RoundtripPostType
  variant?: 'icon-only' | 'outlined'
}

export const RaButtonAddToWishlist = ({
  product,
  variant = 'icon-only',
}: RaButtonWishlistProps) => {
  const { destination, isFamilySite } = useSettings()
  const { enableWishlist, wishlistItems, addToWishlist, removeFromWishlist } =
    useWishlist()
  const isErlebe = getCurrentEnv() === 'ERLEBE'

  const transformedItem = useMemo(() => {
    return makeCartItem(destination, product)
  }, [product])

  const alreadyInWishlist = useMemo(() => {
    if (!wishlistItems?.length) return false
    return wishlistItems.find(item => item.cartId === transformedItem.cartId)
  }, [wishlistItems])

  const wishlistIcon = useMemo(() => {
    if (!alreadyInWishlist) return ICONS.WISHLIST
    if (isFamilySite) return ICONS.WISHLIST_ACTIVE_FAMILY
    return isErlebe ? ICONS.WISHLIST_ACTIVE_ERLEBE : ICONS.WISHLIST_ACTIVE
  }, [isErlebe, alreadyInWishlist])

  const handleWishlist = () => {
    if (alreadyInWishlist) {
      removeFromWishlist(transformedItem)
    } else {
      addToWishlist([transformedItem])
    }
  }

  if (!enableWishlist) return null

  if (variant === 'outlined') {
    return (
      <RaButton
        variant="secondary-dark"
        onClick={handleWishlist}
        tooltip={
          alreadyInWishlist
            ? translate('REMOVE_FROM_WISHLIST')
            : translate('ADD_TO_WISHLIST')
        }
        startIcon={
          <RaIcon
            icon={wishlistIcon}
            color="dark"
          />
        }
      />
    )
  }

  return (
    <RaButton
      variant={'icon'}
      onClick={handleWishlist}
      tooltip={
        alreadyInWishlist
          ? translate('REMOVE_FROM_WISHLIST')
          : translate('ADD_TO_WISHLIST')
      }
      icon={
        <RaIcon
          icon={wishlistIcon}
          color="dark"
        />
      }
    />
  )
}
